<template>
  <div id="trade-list-table" class="trade-list-component">
    <div class="section-card px-5 py-5 card-mobile trade-list-card">
      <h3 v-if="trades.length === 0" class="text-center my-12">
        <v-icon class="d-flex align-center justify-center mt-12 mb-5" x-large
          >fa-regular fa-grin-beam-sweat</v-icon
        >
        {{ $t("noTrades") }}
      </h3>

      <v-data-table
        v-else
        class="trade-list-table"
        style="background-color: rgba(0, 0, 0, 0)"
        hide-default-footer
        :headers="headers"
        :items="trades"
        :options.sync="options"
        :server-items-length="totalElements"
        :loading="this.$store.state.book.loadingTrades"
      >
        <!--template v-slot:item="{ item }" @click="showTrade"></template!-->
        <template v-slot:item.side="{ item }">
          <i :style="`color: ${getTradeSideColor(item.side)}; font-size: 15px;`"
            >{{ getTradeSideText(item.side) }}</i>
        </template>
        <template v-slot:item.mood="{ item }">
          <v-chip style="color: var(--v-mediumGray-base) !important;" class="mood-span" small outlined>
            <v-icon left small>{{ getMoodIcon(item.mood) }}</v-icon>
            {{ getMoodText(item.mood) }}
          </v-chip>
        </template>
        <template v-slot:item.netProfit="{ item }">
          <v-chip
            :class="getNetProfitColor(item.netProfit)"
            style="color: #ffffff"
            ><span class="netProfit-span">{{ item.netProfit }}</span></v-chip
          >
        </template>
        <template v-slot:item.broker="{ item }">
          <i class="rowData">{{ item.broker }}</i>
        </template>
        <template v-slot:item.strategy="{ item }">
          <i v-if="item.strategy != null" class="rowData">{{
            item.strategy.name
          }}</i>
          <i v-else class="rowData">{{ $t("none") }}</i>
        </template>
        <template v-slot:item.asset="{ item }">
          <i class="rowData">{{ item.asset }}</i>
        </template>
        <template v-slot:item.openTime="{ item }">
          <div class="open-time-cell">
            <span class="open-time-cell__date">{{
              formatOpenTimeDate(item.openTime)
            }}</span>
            <span class="open-time-cell__time">{{
              formatOpenTimeClock(item.openTime)
            }}</span>
          </div>
        </template>
        <template v-slot:item.tradeTime="{ item }">
          <i class="rowData">{{ getTradeTime(item.tradeTime) }}</i>
        </template>
        <template v-slot:item.entryPrice="{ item }">
          <i class="rowData">{{ formatEntryPrice(item.entryPrice) }}</i>
        </template>
        <template v-slot:item.closePrice="{ item }">
          <i class="rowData">{{ item.closePrice }}</i>
        </template>
        <template v-slot:item.stopLossPrice="{ item }">
          <i class="rowData">{{ item.stopLossPrice }}</i>
        </template>
        <template v-if="!isMobile" v-slot:item.action="{ item }">
          <div class="flex">
            <v-icon small class="icon mr-3" @click="showTrade(item)"
              >fa-regular fa-eye</v-icon
            >
            <v-icon
              v-if="!shared"
              small
              class="icon mr-3"
              @click="editTrade(item)"
              >fa-regular fa-pen</v-icon
            >
            <v-icon
              v-if="!shared && !item.brokerAccount.verified"
              small
              class="icon"
              @click="deleteTrade(item)"
              >fa-regular fa-trash</v-icon
            >
          </div>
        </template>
      </v-data-table>

      <div v-if="trades.length > 0 && totalElements > 0" class="trade-list-pagination">
        <div class="trade-list-pagination__controls">
          <button
            type="button"
            class="trade-list-pagination__arrow"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >
            <v-icon small>
              fa-chevron-left
            </v-icon>
          </button>

          <template v-for="(page, index) in visiblePages">
            <span
              v-if="page === '...'"
              :key="'ellipsis-' + index"
              class="trade-list-pagination__gap"
            >—</span>
            <button
              v-else
              :key="'page-' + page"
              type="button"
              class="trade-list-pagination__page"
              :class="{ 'trade-list-pagination__page--active': page === currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </template>

          <button
            type="button"
            class="trade-list-pagination__arrow"
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
          >
            <v-icon small>
              fa-chevron-right
            </v-icon>
          </button>
        </div>

        <div class="trade-list-pagination__meta">
          <span class="trade-list-pagination__range">{{ pageRangeText }}</span>
          <select
            class="trade-list-pagination__select"
            :value="pageSize"
            @change="onItemsPerPageChange($event.target.value)"
          >
            <option
              v-for="size in itemsPerPageOptions"
              :key="size"
              :value="size"
            >
              {{ size }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <v-overlay :dark="false" :value="deleteTradeWindow">
      <DeleteTrade @closeDeleteWindow="closeDeleteWindow" :trade="tradeToSee" />
    </v-overlay>
    <v-overlay :dark="false" :value="showTradeWindow">
      <FullTrade @closeShowWindow="closeShowWindow" :trade="tradeToSee" />
    </v-overlay>
    <v-overlay :dark="false" :value="editTradeWindow">
      <EditTrade @closeEditWindow="closeEditWindow" :trade="tradeToSee" />
    </v-overlay>
  </div>
</template>

<script>
import Moods from "./moods";
import { mapMutations } from "vuex";
import DeleteTrade from "./components/DeleteTrade";
import FullTrade from "./components/FullTrade";
import EditTrade from "./components/EditTrade";

export default {
  name: "TradeList",
  components: { EditTrade, FullTrade, DeleteTrade },
  props: {
    shared: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      trades: this.$store.state.book.trades,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["openTime"],
        sortDesc: [true],
      },
      itemsPerPageOptions: [10, 15, 25, 50],
      // Actions variables
      editedIndex: -1,
      editedItem: {
        openTime: "",
        tradeTime: "",
        side: "",
        assetId: 0,
        strategyId: 0,
        brokerAccountId: 0,
        entryPrice: 0,
        closePrice: 0,
        stopLossPrice: 0,
        netProfit: 0,
        commisions: 0,
      },
      defaultItem: {
        openTime: "",
        tradeTime: "",
        side: "",
        assetId: 0,
        strategyId: 0,
        brokerAccountId: 0,
        entryPrice: 0,
        closePrice: 0,
        stopLossPrice: 0,
        netProfit: 0,
        commisions: 0,
      },
      deleteTradeWindow: false,
      showTradeWindow: false,
      editTradeWindow: false,
      tradeToSee: null,
    };
  },
  watch: {
    options: {
      handler() {
        this.changeTradesPageable();
      },
      deep: true,
    },
    "$store.state.book.trades": function () {
      this.trades = this.$store.state.book.trades;
    },
  },
  mounted() {
    this.changeTradesPageable();
  },
  computed: {
    isMobile() {
      return window.innerWidth <= 768;
    },
    headers() {
      let headers = [
        {
          text: this.$t("headers_TradeList.asset"),
          align: "left",
          value: "asset.assetName",
        },
        { text: this.$t("headers_TradeList.side"), value: "side" },
        { text: this.$t("headers_TradeList.openTime"), value: "openTime" },
        { text: this.$t("headers_TradeList.tradeTime"), value: "tradeTime" },
        {
          text: this.$t("headers_TradeList.entryPrice"),
          sortable: false,
          value: "entryPrice",
        },
        { text: this.$t("headers_TradeList.mood"), value: "mood" },
        { text: this.$t("headers_TradeList.strategy"), value: "strategy" },
        { text: this.$t("headers_TradeList.netProfit"), value: "netProfit" },
      ];
      if (!this.isMobile) {
        headers.push({
          text: this.$t("headers_TradeList.actions"),
          value: "action",
          sortable: false,
        });
      }
      return headers;
    },
    totalElements() {
      return this.$store.state.book.totalElements;
    },
    totalPages() {
      const fromStore = this.$store.state.book.totalPages;
      if (fromStore > 0) return fromStore;
      return Math.max(1, Math.ceil(this.totalElements / this.pageSize));
    },
    currentPage() {
      return this.options.page || 1;
    },
    pageSize() {
      return this.options.itemsPerPage || 10;
    },
    pageRangeText() {
      if (this.totalElements === 0) return "0-0 of 0";
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.totalElements);
      return `${start}-${end} of ${this.totalElements}`;
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 5) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      if (current <= 3) {
        return [1, 2, 3, "...", total];
      }

      if (current >= total - 2) {
        return [1, "...", total - 2, total - 1, total];
      }

      return [1, "...", current - 1, current, current + 1, "...", total];
    },
  },
  methods: {
    getTradeSideColor(side) {
      return side.toUpperCase() === "BUY" ? "#008CFF" : "#FF7300";
    },
    getNetProfitColor(netProfit) {
      return netProfit > 0 ? "darkgreen" : "darkred";
    },
    getMoodColor(mood) {
      switch (mood) {
        case "Relaxed":
          return "#4d6556";
        case "Cautious":
          return "#386538";
        case "Concentrated":
          return "#376563";
        case "Confident":
          return "#376563";
        case "Excited":
          return "#28655c";
        case "Anxious":
          return "#28655c";
        case "Happy":
          return "#0f6500";
        case "Optimistic":
          return "#1d5465";
        case "Aggressive":
          return "#651540";
        case "Angry":
          return "#650700";
        case "Bored":
          return "#65412e";
        case "Mad":
          return "#651540";
        case "Nervous":
          return "#651540";
        case "Sad":
          return "#65412e";
        case "Upset":
          return "#651540";
        case "None":
          return "#555555";
        default:
          return "#651540";
      }
    },
    getMoodIcon(mood) {
      switch (mood) {
        case "Relaxed":
          return "fa-laugh-wink";
        case "Cautious":
          return "fa-smile";
        case "Concentrated":
          return "fa-meh-blank";
        case "Confident":
          return "fa-smile-beam";
        case "Excited":
          return "fa-grin-tongue";
        case "Happy":
          return "fa-grin-beam";
        case "Optimistic":
          return "fa-grin-stars";
        case "Aggressive":
          return "fa-meh-rolling-eyes";
        case "Angry":
          return "fa-angry";
        case "Bored":
          return "fa-frown";
        case "Mad":
          return "fa-angry";
        case "Nervous":
          return "fa-grimace";
        case "Sad":
          return "fa-sad-tear";
        case "Upset":
          return "fa-frown-open";
        case "None":
          return "fa-ban";
        default:
          return "fa-flushed";
      }
    },
    getTradeSideText(side) {
      return side.toUpperCase() === "BUY" ? this.$t('Buy') : this.$t('Sell');
    },
    getMoodText(mood) {
      let currentMood = Moods.find((cmood) => cmood.en === mood);
      if (this.$i18n.locale === "en") {
        return currentMood.en;
      } else {
        return currentMood.es;
      }
    },
    getTradeTime(duration) {
      let message = duration.days > 0 ? `${duration.days}d` : "";
      message = `${message} ${duration.hours > 0 ? `${duration.hours}h` : ""}`;
      message = `${message} ${
        duration.minutes > 0 ? `${duration.minutes}m` : ""
      }`;
      message = `${message} ${
        duration.seconds > 0 ? `${duration.seconds}s` : ""
      }`;
      return message;
    },
    formatEntryPrice(price) {
      const num = Number(price);
      if (Number.isNaN(num)) return price;
      return parseFloat(num.toFixed(5)).toString();
    },
    formatOpenTimeDate(date) {
      if (!date || !(date instanceof Date)) return "";
      const monthRaw = date
        .toLocaleDateString(this.$i18n.locale, { month: "short" })
        .replace(".", "");
      const month =
        monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1).toLowerCase();
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    formatOpenTimeClock(date) {
      if (!date || !(date instanceof Date)) return "";
      return `${String(date.getHours()).padStart(2, "0")}:${String(
        date.getMinutes()
      ).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
    },
    ...mapMutations(["setTradesPageable"]),
    changeTradesPageable() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      let pageable = {
        size:
          itemsPerPage === -1
            ? this.$store.state.book.totalElements
            : itemsPerPage,
        page: itemsPerPage === -1 ? 0 : page - 1,
        sort: "openTime,desc",
      };
      if (sortBy && sortBy[0] !== undefined) {
        pageable.sort = sortBy[0] + "," + (sortDesc[0] ? "desc" : "asc");
      }
      this.setTradesPageable(pageable);
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.options = { ...this.options, page };
    },
    onItemsPerPageChange(value) {
      const itemsPerPage = Number(value);
      this.options = { ...this.options, page: 1, itemsPerPage };
    },
    showTrade(trade) {
      this.tradeToSee = trade;
      this.showTradeWindow = true;
    },
    editTrade(trade) {
      this.tradeToSee = trade;
      this.editTradeWindow = true;
    },
    deleteTrade(trade) {
      this.tradeToSee = trade;
      this.deleteTradeWindow = true;
    },
    closeShowWindow() {
      this.showTradeWindow = false;
    },
    closeEditWindow() {
      this.editTradeWindow = false;
    },
    closeDeleteWindow() {
      this.deleteTradeWindow = false;
    },
  },
};
</script>



<style lang="scss" scoped>
@media (max-width: 599px) {
  .card-mobile {
    margin-left: 0px;
  }
}

.trade-list-component {
  .trade-list-card {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  .trade-list-table {
    border: none !important;
    box-shadow: none !important;

    ::v-deep .v-data-table__wrapper {
      border: none !important;
      box-shadow: none !important;
    }

    ::v-deep table {
      border: none !important;
    }

    ::v-deep thead {
      tr {
        th {
          background-color: var(--v-mainGreen-base) !important;
          font-family: 'Avenir', sans-serif;
          font-size: 14px !important;
          font-weight: 600;
          color: #F8F8F8 !important;
          padding: 14px 16px !important;

          * {
            color: #F8F8F8 !important;
          }
        }

        &:first-child th:first-child {
          border-top-left-radius: 8px;
        }

        &:first-child th:last-child {
          border-top-right-radius: 8px;
        }
      }
    }

    ::v-deep tbody {
      tr {
        td {
          padding: 14px 16px !important;
          vertical-align: middle;
        }
      }
    }

    i {
      font-style: normal;
    }
  }

  .trade-list-pagination {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: end;
    gap: 16px;
    padding: 20px 4px 60px 4px;

    &__controls {
      grid-column: 2;
      justify-self: center;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__arrow {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;

      .v-icon {
        color: var(--v-mainBlack-base) !important;
      }

      &:disabled {
        cursor: not-allowed;

        .v-icon {
          color: var(--v-mediumGray-base) !important;
        }
      }
    }

    &__page {
      min-width: 32px;
      height: 32px;
      padding: 0 6px;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: var(--v-mediumGray-base);
      font-family: 'Avenir', sans-serif;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s, color 0.2s;

      &--active {
        background: var(--v-mainGreen-base);
        color: var(--v-buttonText-base);
      }

      &:not(&--active):hover {
        color: var(--v-mainBlack-base);
      }
    }

    &__gap {
      color: var(--v-mediumGray-base);
      font-size: 14px;
      padding: 0 2px;
      user-select: none;
    }

    &__meta {
      grid-column: 3;
      justify-self: end;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
    }

    &__range {
      font-family: 'Avenir', sans-serif;
      font-size: 12px;
      color: var(--v-darkGray-base);
      white-space: nowrap;
    }

    &__select {
      min-width: 64px;
      padding: 6px 28px 6px 12px;
      border: 1px solid var(--v-mainGreen-base);
      border-radius: 8px;
      background-color: var(--v-cardBackground-base);
      font-family: 'Avenir', sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: var(--v-mainBlack-base);
      cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2314161b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 8px center;
      background-size: 14px;

      option {
        background-color: var(--v-cardBackground-base);
        color: var(--v-mainBlack-base);
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(40, 234, 150, 0.25);
      }
    }
  }
}

.theme--dark .trade-list-component {
  .trade-list-pagination__select {
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F8F8F8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  }
}

.open-time-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.35;

  &__date {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-dark-base);
  }

  &__time {
    font-size: 15px;
    font-weight: 400;
    color: var(--v-dark-base);
  }
}

.rowData {
  font-size: 15px;
  color: var(--v-dark-base);
}

.old-filters {
  display: none;
}

.green {
  background-color: var(--v-darkGreen-base) !important;
  color: var(--v-white-base) !important;
}

.darkgreen {
  background-color: var(--v-darkGreen-base) !important;
  color: var(--v-white-base) !important;
}
.red {
  background-color: var(--v-darkRed-base) !important;
  color: var(--v-white-base) !important;
}

.darkred {
  background-color: var(--v-darkRed-base) !important;
  color: var(--v-white-base) !important;
}
@media (max-width: 600px) {
  .mobile-flex {
    flex-direction: row;
    width: 100px;
  }
}
</style>
